'use client';

import { useRouter } from 'next/navigation';
import { ConceptTitle } from './ConceptTitle';
import type { Concept } from '@/types/concept';
import type { Project } from '@/types/project';

interface ConceptHeaderProps {
  concept: Concept;
  project: Project;
}

export function ConceptHeader({ concept, project }: ConceptHeaderProps) {
  const router = useRouter();

  const handleNavigationClick = (conceptId: string) => {
    // Mobile-header title updates automatically once `useParams()` reflects
    // the new conceptId (`useConceptHeaderData` reads the cached concept).
    router.push(`/project/${project.id}/concept/${conceptId}`);
  };

  const prevConceptId = concept.meta?.navigation?.prev_concept_id;
  const nextConceptId = concept.meta?.navigation?.next_concept_id;

  return (
    // Hidden on mobile - navigation is in MobileHeader
    <div className="hidden sm:flex items-center justify-between mb-6">
      <ConceptTitle
        title={concept.attributes.title}
        sequenceNumber={concept.attributes.project_sequence_number}
      />

      {/* Navigation arrows - hidden on lg+ (sidebar has navigation) */}
      <div className="flex items-center gap-1 lg:hidden">
        <button
          onClick={() => prevConceptId && handleNavigationClick(prevConceptId)}
          disabled={!prevConceptId}
          className="p-2 rounded-lg bg-button-simple text-primary border border-button-simple-border hover:bg-button-simple-hover hover:border-button-simple-hover-border hover:scale-110 active:bg-button-simple-active disabled:bg-transparent disabled:border-transparent disabled:text-muted-foreground disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:scale-100 transition-all duration-200"
          aria-label="Previous concept"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>

        <button
          onClick={() => nextConceptId && handleNavigationClick(nextConceptId)}
          disabled={!nextConceptId}
          className="p-2 rounded-lg bg-button-simple text-primary border border-button-simple-border hover:bg-button-simple-hover hover:border-button-simple-hover-border hover:scale-110 active:bg-button-simple-active disabled:bg-transparent disabled:border-transparent disabled:opacity-30 disabled:text-muted-foreground disabled:cursor-not-allowed disabled:hover:bg-transparent disabled:hover:scale-100 transition-all duration-200"
          aria-label="Next concept"
        >
          <svg
            className="w-6 h-6"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}
